import { useQuery } from '@tanstack/react-query';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getDevicePlaneSensorsSummary } from '../api/devicePlane.js';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const RAMP = ['#0d366b', '#104281', '#184f95', '#1c5cab', '#256abf', '#2a78d6', '#3987e5', '#5598e7'];

const TICK = { fontSize: 11, fill: '#898781' };
const TOOLTIP_STYLE = {
  background: '#1a1a19',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 12,
};

function rampColor(v: number, max: number): string {
  if (v <= 0 || max <= 0) return '#222221';
  return RAMP[Math.min(RAMP.length - 1, Math.floor((v / max) * RAMP.length))]!;
}

export function DevicePlaneSensors() {
  const { data } = useQuery({
    queryKey: ['dp-sensors-summary'],
    queryFn: getDevicePlaneSensorsSummary,
    refetchInterval: 60000,
  });

  // Build 7×24 grid from sparse cells.
  const grid: number[][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
  for (const c of data?.motion ?? []) grid[c.dow]![c.hour] = c.count;
  const max = Math.max(...grid.flat());
  const hasMotion = (data?.motion ?? []).length > 0;

  const ldrPoints = (data?.ldrHourly ?? []).map((p) => ({
    t: new Date(p.hour).toLocaleTimeString([], { hour: '2-digit' }),
    v: p.avg,
  }));

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-ink">Device Plane — Sensors</h1>
        <p className="text-sm text-muted mt-0.5">
          Fleet-wide sensor rollups from the telemetry store (365-day retention): motion patterns and ambient light.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Motion heatmap */}
        <div className="bg-surface border border-white/10 rounded-xl p-4">
          <p className="text-sm font-semibold text-ink2 mb-1">Motion heatmap — events by hour × day (last 7 d)</p>
          <p className="text-xs text-muted mb-3">Summed pir readings across every device that reports motion.</p>
          {hasMotion ? (
            <div className="grid gap-0.5" style={{ gridTemplateColumns: '34px repeat(24, 1fr)' }}>
              <span />
              {Array.from({ length: 24 }, (_, h) => (
                <span key={h} className="text-center text-[9px] text-muted">
                  {h % 4 === 0 ? String(h).padStart(2, '0') : ''}
                </span>
              ))}
              {grid.map((row, d) => (
                [
                  <span key={`lab-${d}`} className="text-right pr-1.5 text-[10px] text-muted self-center">{DAYS[d]}</span>,
                  ...row.map((v, h) => (
                    <span
                      key={`${d}-${h}`}
                      title={`${DAYS[d]} ${String(h).padStart(2, '0')}:00 — ${v} motion event(s)`}
                      className="rounded-[3px]"
                      style={{ aspectRatio: '1 / 1', background: rampColor(v, max) }}
                    />
                  )),
                ]
              ))}
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-xs text-muted text-center px-6">
              No motion (pir) telemetry in the last 7 days — the heatmap fills in as devices report motion readings.
            </div>
          )}
        </div>

        {/* Ambient light */}
        <div className="bg-surface border border-white/10 rounded-xl p-4">
          <p className="text-sm font-semibold text-ink2 mb-1">Ambient light — fleet hourly average (last 24 h)</p>
          <p className="text-xs text-muted mb-3">Average ldr reading across all reporting devices.</p>
          {ldrPoints.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={ldrPoints} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
                  <XAxis dataKey="t" tick={TICK} interval="preserveStartEnd" stroke="#383835" />
                  <YAxis tick={TICK} stroke="#383835" />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Area type="monotone" dataKey="v" stroke="#3987e5" fill="rgba(57,135,229,0.15)"
                    dot={ldrPoints.length < 3} name="avg ldr" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-xs text-muted text-center px-6">
              No ambient-light (ldr) telemetry in the last 24 hours.
            </div>
          )}
        </div>
      </div>

      <div className="border border-dashed border-bad rounded-xl p-4 text-sm text-ink2 bg-surface">
        <b className="text-ink">Sensor boards (VL53L1X ToF distance, IR break-beam):</b> the physical sensor boards
        measure live distance, detection counts and calibration state, but they currently report only to the M8
        controller over RS485/UDP — not to the middleware. Their data appears here once the firmware forwards those
        readings as <span className="font-mono text-xs">telemetry.readings</span> keys on the
        <span className="font-mono text-xs"> V1/HARON</span> contract (no middleware change needed — the telemetry
        catchall persists any new key automatically).
      </div>
    </div>
  );
}
